import { Injectable } from '@angular/core';

import { buildEnergyChartKey, ChartDefinition, ChartKey, isEnergyChartKey } from '../models/chart.models';
import { ParallelismMode } from '../models/execution-details.models';
import { HeatmapService } from './heatmap.service';
import { HybridViewMode } from '../../utils/region-utils';

export interface ChartTooltipContext {
  parallelismMode: ParallelismMode;
  hybridViewMode?: HybridViewMode;
}

@Injectable({ providedIn: 'root' })
export class ChartMetadataService {
  constructor(private heatmap: HeatmapService) {}

  getChartDefinitions(
    maxSpeedupAchieved: number,
    maxLoss: number,
    minLoss: number,
    energySensors: string[] = []
  ): ChartDefinition[] {
    return [
      { key: 'eff', title: 'Efficiency', palette: this.heatmap.yellowOrangePurpleBlackPalette, min: 0, max: 100, unit: '%' },
      { key: 'sca', title: 'Scalability', palette: this.heatmap.strictRedBluePalette, min: -100, max: 100, unit: '%' },
      { key: 'str', title: 'Strong Scalability', palette: this.heatmap.strictRedBluePalette, min: -100, max: 100, unit: '%' },
      { key: 'wka', title: 'Weak Scalability', palette: this.heatmap.strictRedBluePalette, min: -100, max: 100, unit: '%' },
      { key: 'imb', title: 'Load Imbalance', palette: this.heatmap.gistHeatPalette, min: 0, max: 100, unit: '%' },
      { key: 'speedup', title: 'Speedup', palette: this.heatmap.divergingBlueRedGradient, min: 0, max: maxSpeedupAchieved, unit: 'x' },
      { key: 'loss', title: 'Speedup Loss', palette: this.heatmap.lossPalette, min: minLoss, max: maxLoss, unit: '' },
      { key: 'perf', title: 'Performance', palette: this.heatmap.infernoPalette, min: 0, max: 100, unit: '' },
      ...energySensors.map(sensor => ({
        key: buildEnergyChartKey(sensor),
        title: sensor,
        palette: this.heatmap.redGradientPalette,
        min: 0,
        max: 100,
        unit: ''
      }))
    ];
  }

  getChartTooltipText(key: ChartKey, context: ChartTooltipContext): string {
    const modelHint = this.getParallelismHint(context);

    switch (key) {
      case 'eff':
        return `Shows parallel efficiency across workload and parallel-configuration combinations. Higher values indicate better use of parallel resources. ${modelHint}`.trim();
      case 'sca':
        return `Shows overall scalability behavior. Use Absolute and Relative modes to compare raw behavior or changes between neighboring configurations. ${modelHint}`.trim();
      case 'str':
        return `Shows strong scalability, indicating how performance changes when the parallel configuration grows for the same workload. ${modelHint}`.trim();
      case 'wka':
        return `Shows weak scalability, indicating how performance changes when workload and parallel resources grow together. ${modelHint}`.trim();
      case 'imb':
        return `Shows load imbalance levels among workers. Higher values indicate a less uniform distribution of work. ${modelHint}`.trim();
      case 'speedup':
        return `Shows speedup relative to the sequential baseline runtime. Values above 1x indicate faster parallel execution. ${modelHint}`.trim();
      case 'loss':
        return `Shows the difference between ideal speedup for the effective parallel resources and the real speedup achieved. Values closer to 0 are better. ${modelHint}`.trim();
      case 'perf':
        return `Shows normalized performance based on runtime. Higher values indicate lower execution time for the workload. ${modelHint}`.trim();
      default:
        if (isEnergyChartKey(key)) {
          return `Shows normalized energy consumption for the selected RAPL sensor. Use EDP to inspect energy-delay product. ${modelHint}`.trim();
        }
        return 'Chart description unavailable.';
    }
  }

  private getParallelismHint(context: ChartTooltipContext): string {
    switch (context.parallelismMode) {
      case 'mpi':
        return 'In the current MPI analysis, each heatmap row corresponds to an MPI rank count.';
      case 'omp':
        return 'In the current OpenMP analysis, each heatmap row corresponds to a core or thread count.';
      case 'hybrid':
        if (context.hybridViewMode === 'total-cores') {
          return 'In the current Hybrid Heatmap view, each row groups all MPI x OpenMP topologies with the same total core count; inspect the cell tooltip for the contributing topologies.';
        }
        return 'In the current Hybrid Topology view, each row is a specific MPI x OpenMP topology such as 2x4 or 4x2.';
      default:
        return 'Each row represents one parallel configuration in the dataset.';
    }
  }
}
